import React, { useState, useMemo } from 'react';
import { formatTime, formatPace } from '../utils/formatters';
import { useStopwatchContext } from '../contexts';
import { WorkoutSession, WorkoutLap } from '../types/workout';
import { Segment } from '../types/segment';

interface PaceDataStorageProps {
  workoutSessions: WorkoutSession[];
  onDeleteSession: (id: string) => void;
}

interface SessionStats {
  totalTime: number;
  totalLaps: number;
  totalSegments: number;
  averagePace: number | null;
}

interface LapMetrics {
  hasCompleteData: boolean;
  distance?: number;
  pace?: number | null;
}

const PaceDataStorage: React.FC<PaceDataStorageProps> = ({ workoutSessions, onDeleteSession }) => {
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [selectedLap, setSelectedLap] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<'date' | 'laps' | 'segments' | 'duration'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [view, setView] = useState<'list' | 'details'>('list'); // 'list' or 'details'
  const context = useStopwatchContext();
  const { paceUnit } = context;

  // For segment distances which are always in meters
  const segmentDistanceUnit = 'm';

  // Format date for display
  const formatDate = (dateString: string | Date): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Format date for details header (without seconds)
  const formatDateForHeader = (dateString: string | Date): string => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
  };

  // Calculate lap metrics (distance and pace) from segments
  const calculateLapMetrics = (
    lap: WorkoutLap,
    segments: Segment[],
    customLapTime?: number
  ): LapMetrics => {
    // Check if we have segments to calculate with
    if (!segments || segments.length === 0) {
      // If no segments, distance is 0 and pace is null
      return { hasCompleteData: true, distance: 0, pace: null };
    }

    // Check if we have all the segment distances
    const allSegmentsHaveDistance = segments.every(segment => segment.distance !== undefined);
    if (!allSegmentsHaveDistance) {
      // Indicate data is incomplete if any segment lacks distance
      return { hasCompleteData: false };
    }

    // Calculate total distance in meters
    const totalDistanceMeters = segments.reduce((sum, segment) => sum + segment.distance, 0);

    // Format to 2 decimal places for display
    const formattedDistance = Math.round(totalDistanceMeters * 100) / 100;

    // Check if the lap already has a valid pace value
    if (lap.pace !== undefined && lap.pace !== null && isFinite(lap.pace) && lap.pace > 0) {
      // If the session's paceUnit matches current display paceUnit, use the stored pace directly
      const sessionPaceUnit = session?.paceUnit || 'mile';
      if (sessionPaceUnit === paceUnit) {
        return {
          hasCompleteData: true,
          distance: formattedDistance,
          pace: lap.pace,
        };
      } else {
        // Convert the existing pace to the current pace unit
        // If stored in min/mile and displaying min/km: multiply by 0.621371
        // If stored in min/km and displaying min/mile: multiply by 1.60934
        const conversionFactor =
          sessionPaceUnit === 'mile' && paceUnit === 'km'
            ? 0.621371 // min/mile to min/km
            : 1.60934; // min/km to min/mile

        return {
          hasCompleteData: true,
          distance: formattedDistance,
          pace: lap.pace * conversionFactor,
        };
      }
    }

    // If no valid stored pace, calculate it from segment data
    // Always calculate pace using total time and total distance
    const totalTime = customLapTime !== undefined ? customLapTime : lap.time;

    // Normalize time value if it's extreme (greater than 1 hour)
    // Cap at 60 minutes for pace calculations to avoid unreasonable values
    const normalizedTime = Math.min(totalTime, 3600000);

    // Skip pace calculation if we have zero or very small distance
    let pace = null;
    if (totalDistanceMeters > 0) {
      // Use the context's calculatePace function which correctly handles the pace unit
      pace = context.calculatePace(normalizedTime, totalDistanceMeters);

      // Sanity check - if pace is extremely high, cap it at 30 minutes per unit
      if (pace > 30) {
        pace = 30;
      }
    }

    return {
      hasCompleteData: true,
      distance: formattedDistance,
      pace: pace,
    };
  };

  // Handle session selection
  const handleSessionSelect = (sessionId: string): void => {
    setSelectedSession(sessionId);
    setSelectedLap(null); // Reset lap selection when changing session
    setView('details'); // Switch to details view
  };

  // Handle lap selection
  const handleLapSelect = (lapNumber: number): void => {
    setSelectedLap(lapNumber === selectedLap ? null : lapNumber);
  };

  // Delete the selected session after confirmation
  const handleDeleteSession = (): void => {
    if (
      selectedSession &&
      window.confirm('Are you sure you want to delete this workout session?')
    ) {
      onDeleteSession(selectedSession);
      setSelectedSession(null);
      setSelectedLap(null);
      setView('list'); // Return to list view after deletion
    }
  };

  // Go back to the list view
  const handleBackToList = (): void => {
    setView('list');
  };

  // Handle sort change
  const handleSortChange = (field: 'date' | 'laps' | 'segments' | 'duration'): void => {
    if (sortBy === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  // Sort sessions based on current sort settings
  const sortedSessions = useMemo(() => {
    if (!workoutSessions || workoutSessions.length === 0) return [];

    return [...workoutSessions].sort((a, b) => {
      let comparison = 0;

      // Declare variables outside case statements to avoid ESLint errors
      let aDuration, bDuration;

      switch (sortBy) {
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case 'laps':
          comparison = a.laps.length - b.laps.length;
          break;
        case 'segments':
          comparison =
            Object.values(a.segments).flat().length - Object.values(b.segments).flat().length;
          break;
        case 'duration':
          aDuration = a.laps.length > 0 ? a.laps[a.laps.length - 1].totalTime : 0;
          bDuration = b.laps.length > 0 ? b.laps[b.laps.length - 1].totalTime : 0;
          comparison = aDuration - bDuration;
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [workoutSessions, sortBy, sortDirection]);

  // Calculate session stats
  const calculateSessionStats = (session: WorkoutSession | null): SessionStats | null => {
    if (!session) return null;

    // Total time - check if laps array exists and has elements
    const laps = session.laps || [];
    const totalTime = laps.length > 0 ? laps[laps.length - 1].totalTime : 0;

    // Total segments
    const segments = session.segments || {};
    const allSegments = Object.values(segments).flat();
    const totalSegments = allSegments.length;

    // Calculate average pace (if segments exist)
    let averagePace = null;
    if (allSegments.length > 0) {
      const totalPace = allSegments.reduce(
        (sum: number, segment: Segment) => sum + (segment.pace || 0),
        0
      );
      averagePace = totalPace / allSegments.length;
    }

    return {
      totalTime,
      totalLaps: laps.length,
      totalSegments,
      averagePace,
    };
  };

  // Render empty state if no sessions
  if (workoutSessions.length === 0) {
    return (
      <div className="pace-data pace-data--empty">
        <h2>No workout sessions found</h2>
        <p>Complete a workout to see your data here.</p>
      </div>
    );
  }

  // Get the selected session object
  const session = selectedSession
    ? (workoutSessions.find(s => s.id === selectedSession) ?? null)
    : null;
  const sessionStats = session ? calculateSessionStats(session) : null;

  // If in details view, render the full details screen
  if (view === 'details') {
    return (
      <div className="pace-data-details-view">
        <div className="page-header">
          <button className="header-back-button" onClick={handleBackToList}>
            <span className="back-arrow">←</span>
          </button>
          <div className="page-title-container">
            <h2 className="page-title">Workout Details</h2>
            <div className="page-subtitle">{session && formatDateForHeader(session.date)}</div>
          </div>
          <button className="header-delete-button" onClick={handleDeleteSession}>
            Delete
          </button>
        </div>

        <div className="pace-data__session-details">
          {/* Session Summary */}
          <div className="pace-data__session-summary">
            <h4>Workout Summary</h4>
            <div className="pace-data__summary-stats">
              <div className="pace-data__summary-stat">
                <span className="pace-data__stat-value">{sessionStats?.totalLaps}</span>
                <span className="pace-data__stat-label">Total Laps</span>
              </div>
              <div className="pace-data__summary-stat">
                <span className="pace-data__stat-value">
                  {sessionStats && formatTime(sessionStats.totalTime)}
                </span>
                <span className="pace-data__stat-label">Total Time</span>
              </div>
              <div className="pace-data__summary-stat">
                <span className="pace-data__stat-value">{sessionStats?.totalSegments}</span>
                <span className="pace-data__stat-label">Segments</span>
              </div>
              {sessionStats?.averagePace && (
                <div className="pace-data__summary-stat">
                  <span className="pace-data__stat-value">
                    {formatPace(sessionStats.averagePace, paceUnit)}
                  </span>
                  <span className="pace-data__stat-label">Avg Pace</span>
                </div>
              )}
            </div>
          </div>

          <div className="pace-data__session-laps">
            <h4>Laps</h4>
            {session && session.laps && session.laps.length > 0 ? (
              <>
                <table
                  className="workout-table"
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    tableLayout: 'fixed',
                    marginBottom: '20px',
                  }}
                >
                  <colgroup>
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '35%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th
                        className="lap-column"
                        style={{
                          fontWeight: 'bold',
                          fontSize: '14px',
                          padding: '8px',
                          backgroundColor: '#222',
                          borderBottom: '1px solid #444',
                          textAlign: 'center',
                        }}
                      >
                        Lap
                      </th>
                      <th
                        className="lap-time-column"
                        style={{
                          fontWeight: 'bold',
                          fontSize: '14px',
                          padding: '8px',
                          backgroundColor: '#222',
                          borderBottom: '1px solid #444',
                          textAlign: 'center',
                        }}
                      >
                        Lap Time
                      </th>
                      <th
                        className="total-time-column"
                        style={{
                          fontWeight: 'bold',
                          fontSize: '14px',
                          padding: '8px',
                          backgroundColor: '#222',
                          borderBottom: '1px solid #444',
                          textAlign: 'center',
                        }}
                      >
                        Total Time
                      </th>
                      <th
                        className="pace-column"
                        style={{
                          fontWeight: 'bold',
                          fontSize: '14px',
                          padding: '8px',
                          backgroundColor: '#222',
                          borderBottom: '1px solid #444',
                          textAlign: 'center',
                        }}
                      >
                        Pace
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {session.laps.map((lap: WorkoutLap, index: number) => {
                      // Calculate lap metrics if we have segments for this lap
                      const lapSegments =
                        session.segments && session.segments[lap.number]
                          ? session.segments[lap.number]
                          : [];

                      // Calculate the correct lap time, prioritizing lap.time
                      let calculatedLapTime: number;

                      // Check if lap.time is valid and seems reasonable (e.g., > 0)
                      if (typeof lap.time === 'number' && lap.time > 0) {
                        calculatedLapTime = lap.time;
                      } else if (index > 0 && session.laps[index - 1]) {
                        // Fallback to calculation if lap.time is invalid/missing and it's not the first lap
                        calculatedLapTime = lap.totalTime - session.laps[index - 1].totalTime;
                        // Add a warning if this fallback is used frequently, it might indicate data inconsistency
                        if (calculatedLapTime <= 0) {
                          console.warn(
                            `Lap ${lap.number}: Calculated lap time (${calculatedLapTime}ms) is invalid using subtraction method. Falling back to lap.time (${lap.time}ms) or 0.`
                          );
                          calculatedLapTime =
                            typeof lap.time === 'number' && lap.time > 0 ? lap.time : 0;
                        }
                      } else {
                        // First lap or other edge case, rely on lap.time or default to 0
                        calculatedLapTime =
                          typeof lap.time === 'number' && lap.time > 0 ? lap.time : 0;
                        if (calculatedLapTime === 0) {
                          console.warn(
                            `Lap ${lap.number}: Could not determine a valid lap time. Defaulting to 0.`
                          );
                        }
                      }

                      // Final reasonableness check (e.g., cap at 1 hour)
                      const isLapTimeReasonable =
                        calculatedLapTime < 3600000 && calculatedLapTime >= 0; // Allow 0 here
                      const correctedLapTime = isLapTimeReasonable
                        ? calculatedLapTime
                        : typeof lap.time === 'number' && lap.time > 0 && lap.time < 3600000
                          ? lap.time
                          : 0; // Final fallback

                      // Pass the determined lap time to calculateLapMetrics
                      const lapMetrics = calculateLapMetrics(lap, lapSegments, correctedLapTime);

                      return (
                        <React.Fragment key={lap.number}>
                          <tr
                            className={`lap-row ${selectedLap === lap.number ? 'selected-lap' : ''}`}
                            onClick={() => handleLapSelect(lap.number)}
                            style={{
                              backgroundColor:
                                selectedLap === lap.number
                                  ? '#2c3548'
                                  : index % 2 === 0
                                    ? '#1a1a1a'
                                    : 'transparent',
                              cursor: 'pointer',
                              borderBottom: '1px solid #333',
                            }}
                          >
                            <td
                              className="lap-column"
                              style={{
                                fontSize: '14px',
                                padding: '8px',
                                textAlign: 'center',
                                fontWeight: selectedLap === lap.number ? 'bold' : 'normal',
                                position: 'relative',
                              }}
                            >
                              {selectedLap === lap.number && (
                                <span
                                  style={{
                                    position: 'absolute',
                                    left: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    fontSize: '12px',
                                    color: '#a8c0ff',
                                  }}
                                >
                                  ▶
                                </span>
                              )}
                              <span>{lap.number}</span>
                            </td>
                            <td
                              className="lap-time-column"
                              style={{
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                padding: '8px',
                                textAlign: 'center',
                                fontWeight: selectedLap === lap.number ? 'bold' : 'normal',
                              }}
                            >
                              {formatTime(correctedLapTime)}
                            </td>
                            <td
                              className="total-time-column"
                              style={{
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                padding: '8px',
                                textAlign: 'center',
                                fontWeight: selectedLap === lap.number ? 'bold' : 'normal',
                              }}
                            >
                              {formatTime(lap.totalTime)}
                            </td>
                            <td
                              className="pace-column"
                              style={{
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                padding: '8px',
                                textAlign: 'center',
                                fontWeight: selectedLap === lap.number ? 'bold' : 'normal',
                              }}
                            >
                              {lapMetrics.hasCompleteData && lapMetrics.pace !== null
                                ? formatPace(lapMetrics.pace, paceUnit)
                                : '--:--'}
                            </td>
                          </tr>

                          {/* Segments display */}
                          {selectedLap === lap.number &&
                            session.segments &&
                            session.segments[lap.number] &&
                            session.segments[lap.number].length > 0 && (
                              <tr>
                                <td colSpan={4} style={{ padding: '0' }}>
                                  <div
                                    style={{
                                      margin: '0 10px 10px 10px',
                                      padding: '10px',
                                      backgroundColor: '#1c2333',
                                      borderRadius: '4px',
                                      border: '1px solid #444',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                    }}
                                  >
                                    <h5
                                      style={{
                                        margin: '0 0 10px 0',
                                        borderBottom: '1px solid #444',
                                        paddingBottom: '5px',
                                        color: '#a8c0ff',
                                      }}
                                    >
                                      Segments for Lap {lap.number}
                                    </h5>
                                    <table
                                      style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        tableLayout: 'fixed',
                                        marginTop: '5px',
                                      }}
                                    >
                                      <colgroup>
                                        <col style={{ width: '15%' }} />
                                        <col style={{ width: '25%' }} />
                                        <col style={{ width: '25%' }} />
                                        <col style={{ width: '35%' }} />
                                      </colgroup>
                                      <thead>
                                        <tr>
                                          <th
                                            style={{
                                              fontWeight: 'bold',
                                              fontSize: '14px',
                                              padding: '8px',
                                              backgroundColor: '#202840',
                                              borderBottom: '1px solid #444',
                                              textAlign: 'center',
                                            }}
                                          >
                                            Seg
                                          </th>
                                          <th
                                            style={{
                                              fontWeight: 'bold',
                                              fontSize: '14px',
                                              padding: '8px',
                                              backgroundColor: '#202840',
                                              borderBottom: '1px solid #444',
                                              textAlign: 'center',
                                            }}
                                          >
                                            Distance
                                          </th>
                                          <th
                                            style={{
                                              fontWeight: 'bold',
                                              fontSize: '14px',
                                              padding: '8px',
                                              backgroundColor: '#202840',
                                              borderBottom: '1px solid #444',
                                              textAlign: 'center',
                                            }}
                                          >
                                            Time
                                          </th>
                                          <th
                                            style={{
                                              fontWeight: 'bold',
                                              fontSize: '14px',
                                              padding: '8px',
                                              backgroundColor: '#202840',
                                              borderBottom: '1px solid #444',
                                              textAlign: 'center',
                                            }}
                                          >
                                            Pace
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {session.segments[lap.number].map(
                                          (segment: Segment, segIndex: number) => (
                                            <tr
                                              key={segment.id}
                                              style={{
                                                backgroundColor:
                                                  segIndex % 2 === 0 ? '#272f42' : '#1e2535',
                                                borderBottom:
                                                  segIndex < session.segments[lap.number].length - 1
                                                    ? '1px solid #333'
                                                    : 'none',
                                              }}
                                            >
                                              <td
                                                style={{
                                                  fontSize: '14px',
                                                  padding: '8px',
                                                  textAlign: 'center',
                                                }}
                                              >
                                                {segment.id ? segment.id.split('-')[0] : ''}
                                              </td>
                                              <td
                                                style={{
                                                  fontFamily: 'monospace',
                                                  fontSize: '14px',
                                                  padding: '8px',
                                                  textAlign: 'center',
                                                }}
                                              >
                                                {segment.distance}
                                                {segmentDistanceUnit}
                                              </td>
                                              <td
                                                style={{
                                                  fontFamily: 'monospace',
                                                  fontSize: '14px',
                                                  padding: '8px',
                                                  textAlign: 'center',
                                                }}
                                              >
                                                {formatTime(segment.time)}
                                              </td>
                                              <td
                                                style={{
                                                  fontFamily: 'monospace',
                                                  fontSize: '14px',
                                                  padding: '8px',
                                                  textAlign: 'center',
                                                }}
                                              >
                                                {segment.pace
                                                  ? formatPace(segment.pace, paceUnit)
                                                  : '--:--'}
                                              </td>
                                            </tr>
                                          )
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </>
            ) : (
              <div className="pace-data__empty-message">No laps recorded for this workout.</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Workout list view - table only, no header (header is in ReviewModeView)
  return (
    <div className="pace-data">
      <div className="pace-data__table-container">
        <table className="pace-data__table">
          <thead>
            <tr>
              <th
                className={sortBy === 'date' ? `sorted ${sortDirection}` : ''}
                onClick={() => handleSortChange('date')}
              >
                Date
              </th>
              <th
                className={sortBy === 'duration' ? `sorted ${sortDirection}` : ''}
                onClick={() => handleSortChange('duration')}
              >
                Duration
              </th>
              <th
                className={sortBy === 'laps' ? `sorted ${sortDirection}` : ''}
                onClick={() => handleSortChange('laps')}
              >
                Laps
              </th>
              <th
                className={sortBy === 'segments' ? `sorted ${sortDirection}` : ''}
                onClick={() => handleSortChange('segments')}
              >
                Segments
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedSessions.map((session: WorkoutSession) => {
              const stats = calculateSessionStats(session);
              return (
                <tr key={session.id} onClick={() => handleSessionSelect(session.id)}>
                  <td className="pace-data__table-date">{formatDate(session.date)}</td>
                  <td className="pace-data__table-duration">
                    {stats && formatTime(stats.totalTime)}
                  </td>
                  <td className="pace-data__table-laps">{stats?.totalLaps}</td>
                  <td className="pace-data__table-segments">{stats?.totalSegments}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaceDataStorage;
